"use client";

import Script from "next/script";
import { useIntersection } from "./use-intersection";
import { useRef, useCallback, useEffect, useState } from "react";
import { clsx } from "clsx";
import { twMerge } from "tailwind-merge";

export type JTPDataLayerItem = {
  topVacationDestinations: string[];
  showFlightsHotelsRouteMapNative: boolean;
  showFlightsHotelsFareSaleNative: boolean;
  device_category?: string;
};

declare global {
  var JTPdataLayer: JTPDataLayerItem[] | undefined;
  var jtpsdk:
    | {
        crossSell?: {
          renderBooker: (config: { type: string; container: string }) => void;
          renderPackages: (container: string) => void;
        };
      }
    | undefined;
}

/**
 * Properties for the JTPContainer component.
 */
export type JTPContainerProperties = {
  /**
   * Class name for the container.
   */
  className?: string;
  /**
   * Optional DOM target ID. Defaults to contentId for existing consumers.
   */
  containerId?: string;
  /**
   * The SDK product type, also used as the default DOM target ID.
   */
  contentId:
    | "cars"
    | "deals-insurance"
    | "flights-cruise"
    | "flights-hotel"
    | "hotels"
    | "jtp-deals-home";
  /**
   * The type of content to render (booker or marketing).
   */
  contentType?: "booker" | "marketing";
  /**
   * The URL for the JTP SDK script.
   */
  scriptUrl: string;
  /**
   * The URL for the tracking script.
   */
  trackingScriptUrl: string;
};

const jtpSDKLoadedState = { current: false };

const once = (function_: () => void) => {
  let hasBeenCalled = false;
  return () => {
    if (hasBeenCalled) {
      return;
    }
    function_();
    hasBeenCalled = true;
  };
};

const onReady = once(() => {
  // eslint-disable-next-line unicorn/no-global-object-property-assignment -- 3rd party must be global
  globalThis.JTPdataLayer = [
    {
      device_category: "desktop",
      showFlightsHotelsFareSaleNative: true,
      showFlightsHotelsRouteMapNative: true,
      topVacationDestinations: [],
    },
  ];
});

export const JTPContainer = ({
  className,
  containerId,
  contentId,
  contentType = "booker",
  scriptUrl,
  trackingScriptUrl,
}: JTPContainerProperties) => {
  const targetId = containerId ?? contentId;
  const reference = useRef<HTMLDivElement>(null);
  const { hasIntersected } = useIntersection({
    ref: reference,
    threshold: 0.1,
  });

  const [isPackagesRendered, setIsPackagesRendered] = useState(false);
  const [isSDKReady, setIsSDKReady] = useState(jtpSDKLoadedState.current);

  const renderPackages = useCallback(() => {
    if (isPackagesRendered || !globalThis.jtpsdk) {
      return;
    }

    const container = document.querySelector(`#${targetId}`);
    if (!container) {
      return;
    }

    try {
      if (contentType === "booker") {
        globalThis.jtpsdk.crossSell?.renderBooker({
          container: `#${targetId}`,
          type: contentId,
        });
      } else {
        globalThis.jtpsdk.crossSell?.renderPackages(`#${targetId}`);
      }

      setIsPackagesRendered(true);
    } catch {
      /**
       * Want to verify what legacy does/did so we know what to do with an error
       */
    }
  }, [contentId, contentType, isPackagesRendered, targetId]);

  const handleJTPSDKLoad = useCallback(() => {
    jtpSDKLoadedState.current = true;
    setIsSDKReady(true);
  }, []);

  useEffect(() => {
    if (isSDKReady && !isPackagesRendered) {
      renderPackages();
    }
  }, [isSDKReady, renderPackages, isPackagesRendered]);

  const containerClassName = twMerge(
    clsx(
      `
        mx-auto min-h-[296px] w-full

        md:min-h-[193px]

        lg:min-h-[121px]
      `,
      className,
      {
        booker: contentType === "booker",
      },
    ),
  );
  return (
    <div className={containerClassName} ref={reference}>
      {hasIntersected && (
        <>
          <Script
            id={`jtp-tracking-${targetId}`}
            onReady={onReady}
            src={trackingScriptUrl}
            strategy="afterInteractive"
          />

          <Script
            id={`jtp-sdk-${targetId}`}
            onLoad={handleJTPSDKLoad}
            src={scriptUrl}
            strategy="afterInteractive"
          />
        </>
      )}

      <div
        className={
          contentId === "jtp-deals-home"
            ? `
              min-h-340

              md:min-h-160
            `
            : "relative block overflow-visible rounded-lg p-4"
        }
        data-fs-element={contentId}
        data-jtp-container="true"
        id={targetId}
      />
    </div>
  );
};
